import { useCallback, useMemo, useState } from 'react';
import { type TranslateRawFunc } from '#common/i18n/edit-mode.tsx';
import { anonymousRegistrationSchema, verifyCodeSchema } from '#speaker-shop/models/anonymous-registration';
import { registerAnonymous, verifyAnonymous } from '#speaker-shop/utils/audio-generation-api.client.ts';

type FormState = 'email' | 'code';

interface UseAnonymousRegistrationControllerOptions {
    rawT: TranslateRawFunc;
    locale: string;
    onVerified?: () => void;
}

interface FieldErrors {
    email?: string[];
    marketingConsent?: string[];
    code?: string[];
    form?: string[];
}

function normalizeErrorArray(value: unknown): string[] | undefined {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string');
    }

    if (typeof value === 'string' && value) {
        return [ value ];
    }

    return undefined;
}

export function useAnonymousRegistrationController(
    { rawT, locale, onVerified }: UseAnonymousRegistrationControllerOptions,
) {
    const [ formState, setFormState ] = useState<FormState>('email');
    const [ email, setEmail ] = useState('');
    const [ code, setCode ] = useState('');
    const [ marketingConsent, setMarketingConsent ] = useState(false);
    const [ isSubmittingEmail, setIsSubmittingEmail ] = useState(false);
    const [ isSubmittingCode, setIsSubmittingCode ] = useState(false);
    const [ fieldErrors, setFieldErrors ] = useState<FieldErrors>({});

    const emailSchema = useMemo(() => anonymousRegistrationSchema(rawT), [ rawT ]);
    const codeSchema = useMemo(() => verifyCodeSchema(rawT), [ rawT ]);

    const clearErrors = useCallback(() => {
        setFieldErrors({});
    }, []);

    const submitEmail = useCallback(async () => {
        clearErrors();

        const parsed = emailSchema.safeParse({
            email,
            marketingConsent: marketingConsent ? 'on' : undefined
        });

        if (!parsed.success) {
            const flat = parsed.error.flatten();
            setFieldErrors({
                email: flat.fieldErrors.email,
                marketingConsent: flat.fieldErrors.marketingConsent,
                form: flat.formErrors
            });
            return false;
        }

        setIsSubmittingEmail(true);
        try {
            const result = await registerAnonymous(parsed.data.email, locale);

            if (result.success) {
                setEmail(result.email ?? parsed.data.email);
                setFormState('code');
                setFieldErrors({});
                return true;
            }

            if (result.requireLogin) {
                setFieldErrors({
                    form: [ rawT('audio-generation:anonymous-registration.require-login', {
                        email: result.email ?? parsed.data.email
                    }) ]
                });
                return false;
            }

            setFieldErrors({
                email: normalizeErrorArray(result.fieldErrors?.email),
                marketingConsent: normalizeErrorArray(result.fieldErrors?.marketingConsent),
                form: normalizeErrorArray(result.formErrors ?? result.error)
            });
            return false;
        } finally {
            setIsSubmittingEmail(false);
        }
    }, [ clearErrors, email, emailSchema, locale, marketingConsent, rawT ]);

    const submitCode = useCallback(async () => {
        clearErrors();

        const parsed = codeSchema.safeParse({ email, code });
        if (!parsed.success) {
            const flat = parsed.error.flatten();
            setFieldErrors({
                code: flat.fieldErrors.code,
                form: flat.formErrors
            });
            return false;
        }

        setIsSubmittingCode(true);
        try {
            const result = await verifyAnonymous(parsed.data.email, parsed.data.code);

            if (result.success) {
                setFieldErrors({});
                onVerified?.();
                return true;
            }

            setFieldErrors({
                code: normalizeErrorArray(result.fieldErrors?.code),
                form: normalizeErrorArray(result.formErrors ?? result.error)
            });
            return false;
        } finally {
            setIsSubmittingCode(false);
        }
    }, [ clearErrors, code, codeSchema, email, onVerified ]);

    const reset = useCallback(() => {
        setFormState('email');
        setEmail('');
        setCode('');
        setMarketingConsent(false);
        setFieldErrors({});
        setIsSubmittingEmail(false);
        setIsSubmittingCode(false);
    }, []);

    return {
        formState,
        email,
        code,
        marketingConsent,
        isSubmittingEmail,
        isSubmittingCode,
        fieldErrors,
        setEmail,
        setCode,
        setMarketingConsent,
        submitEmail,
        submitCode,
        reset
    };
}
